///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { GtthPosition } from '../position';
import { LinkFeature } from '../link/link-feature';
import { SectorFeatureView } from './view/sector-feature-view';
import { GtthHistoryService } from '../../engine/history-service';
import { GtthChangeSectorAngleCommand } from '../../engine/commands/link/change-sector-angle-command';
import { ExtendedProductdata, GtthStepGroupEnum } from '../../../../generated/shared-types.model';
import { GtthSectorObstruction } from './sector-obstruction.enum';
import { DeviceSectorsFeature } from './device-sectors-feature';
import { SectorConstants } from './view/sector.constants';
import { isDefined } from '@siklu/sui-lib/utils';

export class SectorFeature {
    public obstructionAngle = 0;
    public obstructionDirection: GtthSectorObstruction;

    private view: SectorFeatureView;

    private p2mpSectorIndex: number;
    private sectorRadius: number;
    private sectorAngle: number;
    private sectorFreq: number;
    private isSelect = false;

    private readonly locationName: string;
    private readonly position: GtthPosition;
    private sectorLinks: LinkFeature[];

    private activeByBom = false;

    private customColor = SectorConstants.SECTOR_DEFAULT_COLOR;

    constructor(
        private readonly deviceSector: DeviceSectorsFeature,
        private readonly sectorIndex: number,
        private readonly sectorInDevice: number,
        private readonly layerIndex: number,
        position: GtthPosition,
        private readonly historyService: GtthHistoryService
    ) {
        this.position = position;
        this.sectorLinks = [];
        this.locationName = this.historyService.logicalTopology.getStation(deviceSector.getSectorStationId()).getName();
    }

    static getSectorIndexesFromAggregatedIndex(index: number): number[] {
        return [index < 10 ? 0 : Math.floor(index / 10), index % 10];
    }

    public setActiveByBom(value: boolean): void {
        this.activeByBom = value;
    }

    public setObstruction(obstructionAngle: number, obstructionDirection: GtthSectorObstruction): void {
        this.obstructionAngle = obstructionAngle;
        this.obstructionDirection = obstructionDirection;
    }

    public generateAggregatedSectorIndex(): number {
        return this.sectorIndex * 10 + this.sectorInDevice;
    }

    public _isActiveSector(): boolean {
        return this.activeByBom || this.sectorLinks.length > 0;
    }

    public _isFrozen(): boolean {
        return this.sectorLinks.some((link) => link.isUserForced());
    }

    _draw(): void {
        const isFreezed = this._isFrozen();

        const activeWithoutLinks = this.activeByBom && this.sectorLinks.length === 0;
        const activeWithLinks = this.sectorLinks.length > 0;

        if (!this.view) {
            this.p2mpSectorIndex = 0;
            if (this.sectorLinks && this.sectorLinks.length > 0) {
                this.p2mpSectorIndex = this.sectorLinks[0].getP2mpRepSectorIndex();
            }
            this.view = new SectorFeatureView(
                this.locationName,
                this.sectorInDevice,
                this.p2mpSectorIndex,
                this.position,
                this.sectorAngle,
                this.sectorRadius,
                this.sectorFreq,
                isFreezed,
                this.customColor,
                this.obstructionAngle,
                this.obstructionDirection
            );

            this.view.setActiveWithLinks(activeWithLinks);
            this.view.setActiveWithoutLinks(activeWithoutLinks);
            this.view.setIsInActiveLayer(this.isInActiveLayer(), this.historyService.selectedStepNumber);

            this.view.draw(this.historyService.map, this.historyService.projectUserSessionService.isUseEngineColors);
            this.initListeners();
        } else {
            this.view.setParams(this.locationName, this.sectorAngle, this.sectorRadius, this.sectorFreq, isFreezed, this.customColor);
            this.view.setActiveWithLinks(activeWithLinks);
            this.view.setActiveWithoutLinks(activeWithoutLinks);
            this.view.setIsInActiveLayer(this.isInActiveLayer(), this.historyService.selectedStepNumber);
            this.view.draw(this.historyService.map, this.historyService.projectUserSessionService.isUseEngineColors);
        }
    }

    _hide(): void {
        if (this.view) {
            this.destroyListeners();
            this.view.hide();
            this.view = null;
        }
    }

    refreshColor(): void {
        if (this.view) {
            const isFreezed = this.sectorLinks.some((link) => link.isUserForced());
            this.view.setIsUserForced(isFreezed);
            this.view.setCustomColor(this.customColor);
            this.view.setIsInActiveLayer(this.isInActiveLayer(), this.historyService.selectedStepNumber);
            this.view.changeColor(this.historyService.projectUserSessionService.isUseEngineColors);
        }
    }

    initListeners(): void {
        if (!this.view) {
            return;
        }
        this.view.registerClickListener((mouseEvent) => {
            if (this.historyService.isEditModeEnabled && this.historyService.selectedStepNumber === GtthStepGroupEnum.LOCATIONS_AND_LOSES) {
                this.historyService.ngZone.run(() => {
                    google.maps.event.trigger(this.historyService.map, 'click', mouseEvent);
                });
            }
        });

        if (this.sectorLinks.length === 0) {
            return;
        }

        this.view.registerRightClickListener((mouseEvent) => {
            this.historyService.ngZone.run(() => {
                this.historyService.infoWindowsService.openSectorInfoWindow(
                    this.generateAggregatedSectorIndex(),
                    GtthPosition.fromEventObject(mouseEvent)
                );
            });
        });

        this.view.registerDoubleClickListener((mouseEvent) => {
            if (this.historyService.isEditModeEnabled) {
                mouseEvent.stop();
                this.historyService.ngZone.run(() => {
                    this.triggerSelect();
                });
            }
        });
    }

    destroyListeners(): void {
        if (this.view) {
            this.view.destroyListeners();
        }
    }

    setFrequency(frequency: number): void {
        this.sectorFreq = frequency;
    }

    setAngle(angle: number): void {
        const newAngle = (360 + angle) % 360;
        if (!isDefined(angle) || this.sectorAngle === newAngle) {
            return;
        }
        this.sectorAngle = newAngle;
        if (this.view) {
            this.view.setAngle(this.sectorAngle);

            this.angleChangeListener(this.sectorAngle);
        }
    }

    angleChangeListener(angle: number): void {
        this.historyService.interactiveWindowsService.sectorDirection(this.generateAggregatedSectorIndex(), angle);
    }

    _addLink(link: LinkFeature): void {
        this.customColor = link.getColor();
        this.sectorAngle = link.getSectorDirectionAngle();
        this.sectorFreq = link.getTxFrequency();

        this.sectorLinks.push(link);
        link.refreshColor();
    }

    public recalculateSectorDistance(usedProduct: number) {
        const usedProductThresholds = this.historyService.logicalTopology.selectedProducts[this.layerIndex]
            .filter((product: ExtendedProductdata) => product.usedProd === usedProduct)
            .map((product) => product.threshold);

        this.sectorRadius = usedProductThresholds?.length ? Math.max(...usedProductThresholds) : 0;
    }

    _removeLink(link: LinkFeature): void {
        this.sectorLinks = this.sectorLinks.filter((sectorLink) => sectorLink.getId() !== link.getId());
    }

    hasLink(link: LinkFeature): boolean {
        if (this.sectorLinks) {
            return this.sectorLinks.some((sectorLink: LinkFeature) => sectorLink.getId() === link.getId());
        }
        return false;
    }

    triggerSelect(): void {
        if (this.isSelect) {
            this.unselect();
        } else {
            this.select();
        }
    }

    getAngle(): number {
        return (360 + this.sectorAngle) % 360;
    }

    getSectorIndex(): number {
        return this.sectorIndex;
    }

    getLinks(): LinkFeature[] {
        return this.sectorLinks;
    }

    getSectorInDevice(): number {
        return this.sectorInDevice;
    }

    select(): void {
        if (!this.historyService.isEditModeEnabled || this.isSelect) {
            return;
        }
        this.initRotationListeners();
        this.isSelect = true;
        this.historyService.logicalTopology.addSelectedSector(this);
        this.view.triggerSelected(this.isSelect, this.historyService.projectUserSessionService.isUseEngineColors);

        this.historyService.interactiveWindowsService.sectorSelected(this.generateAggregatedSectorIndex(), true);
    }

    unselect(): void {
        if (!this.isSelect) {
            return;
        }
        this.isSelect = false;
        this.historyService.logicalTopology.removeSelectedSector(this.generateAggregatedSectorIndex());
        this.view.triggerSelected(this.isSelect, this.historyService.projectUserSessionService.isUseEngineColors);

        this.historyService.interactiveWindowsService.sectorSelected(this.generateAggregatedSectorIndex(), false);
        this.destroyRotationListeners();
    }

    private initRotationListeners(): void {
        this.view.addRotationListener('mousedown', (mouseEvent: any) => {
            this.startSectorRotate(mouseEvent);
        });

        this.view.addRotationListener('mouseup', () => {
            this.endSectorRotate();
        });

        this.view.addRotationListener('mouseout', () => {
            this.endSectorRotate();
        });

        this.view.addRotationListener('mousemove', (mouseEvent: any) => {
            this.sectorRotation(mouseEvent);
        });
    }

    private destroyRotationListeners(): void {
        this.view.destroyListener('mousedown');
        this.view.destroyListener('mouseup');
        this.view.destroyListener('mouseout');
        this.view.destroyListener('mousemove');
    }

    private startSectorRotate(mouseEvent): void {
        this.view.startSectorRotate(mouseEvent);
        this.historyService.closeInfoWindows();
    }

    private sectorRotation(mouseEvent): void {
        const newAngle = this.view.sectorRotation(mouseEvent);
        if (newAngle === undefined) {
            return;
        }

        const directionChanged = this.deviceSector.changeSectorsDirection(this.sectorInDevice, newAngle);

        if (directionChanged) {
            if (isDefined(this.angleChangeListener)) {
                this.angleChangeListener(this.sectorAngle % 360);
            }
        } else {
            this.view.getSectorMouseDelta(mouseEvent);
        }
    }

    private endSectorRotate(): void {
        if (this.view.endSectorRotate()) {
            this.historyService.doCommand(
                this.historyService.command(GtthChangeSectorAngleCommand).create(this.sectorIndex, this.sectorInDevice, this.sectorAngle)
            );
        }
    }

    private isInActiveLayer(): boolean {
        return (
            this.historyService.logicalTopology.activeLayerIdx === -1 ||
            this.historyService.logicalTopology.activeLayerIdx === this.layerIndex
        );
    }
}
